import {isFeatureEnabled} from 'feature-flags';
import {FormControl, FormField, IconButton, openDialog} from 'platform/components';
import {Box, HStack, Icon, Show, Space, Text, VStack} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';

import {FieldValues, Path, UseFormReturn, useWatch} from 'react-hook-form';

import {isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetVehicleMileageQuery, useGetVehicleMileageValidationQuery} from '@dms/api/sales';
import featureFlags from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {Nullish, RequiredTestIdProps, suffixTestId, useDebouncedValue} from 'shared/web';

import {is404ApiError} from '../../utils/is404ApiError';
import {OdometerHistory} from './components/OdometerHistory';

const INT_32_MAX = 2147483647;
interface MileageInputProps<TFieldValues extends FieldValues = FieldValues>
  extends RequiredTestIdProps {
  control: FormControl<TFieldValues>;
  formApi: UseFormReturn<TFieldValues>;
  name: Path<TFieldValues>;
  vehicleId: string | Nullish;
  shouldShowDelta?: boolean;
  shouldShowWarning?: boolean;
  isRequired?: boolean;
  isRecommended?: boolean;
  label?: string;
  onChange?: (value: number | null) => void;
  isDisabled?: boolean;
}

export function MileageInput<TFieldValues extends FieldValues = FieldValues>(
  props: MileageInputProps<TFieldValues>
) {
  const isMileageHistoryEnabled = isFeatureEnabled(featureFlags.VEHICLE_MILEAGE_HISTORY);

  const defaultValue = props.control._defaultValues?.[props.name];
  const hasError = isNotNil(props.formApi.formState.errors[props.name]);
  const mileage = useWatch({control: props.control, name: props.name}) as number | Nullish;
  const formatNumber = useFormatNumber();
  const debouncedMileage = useDebouncedValue(mileage, 1000);

  const didUserChangedMileage = mileage !== defaultValue;

  const {error: errorCurrentMileage, isLoading: isLoadingCurrentMileage} =
    useGetVehicleMileageQuery(
      {vehicleId: props.vehicleId ?? ''},
      {skip: isNilOrEmpty(props.vehicleId) || !isMileageHistoryEnabled}
    );

  const {
    data: vehicleMileage,
    isLoading: isLoadingVehicleMileage,
    isFetching: isFetchingVehicleMileage,
  } = useGetVehicleMileageValidationQuery(
    {vehicleId: props.vehicleId ?? '', mileage: debouncedMileage ?? 0},
    {
      skip:
        isNilOrEmpty(debouncedMileage) ||
        isNilOrEmpty(props.vehicleId) ||
        is404ApiError(errorCurrentMileage) ||
        !isMileageHistoryEnabled,
    }
  );

  const isOverflowing = (debouncedMileage ?? 0) > INT_32_MAX;

  const isLoading = isLoadingVehicleMileage || isLoadingCurrentMileage || isFetchingVehicleMileage;
  const shouldValidate =
    isNotNil(vehicleMileage) &&
    isNotNil(mileage) &&
    debouncedMileage === mileage &&
    !isLoading &&
    !hasError;

  const shouldDisplayDelta = shouldValidate && props.shouldShowDelta && vehicleMileage?.delta > 0;
  const shouldDisplayWarning =
    shouldValidate && props.shouldShowWarning && vehicleMileage?.delta < 0;
  const shouldDisplayNoPriorMileage = is404ApiError(errorCurrentMileage);

  const handleOpenOdometerHistory = () => {
    openDialog(<OdometerHistory vehicleId={props.vehicleId} />, {
      title: i18n.t('entity.vehicle.labels.odometerHistory'),
      size: 'large',
    });
  };

  return (
    <VStack spacing={1}>
      <HStack spacing={2}>
        <Box flex={1}>
          <FormField
            control={props.control}
            name={props.name}
            type="number"
            isRequired={props.isRequired}
            isRecommended={props.isRecommended}
            suffix={i18n.t('general.metric.km')}
            label={props.label}
            data-testid={suffixTestId(props.label ?? 'milage', props)}
            onChange={props.onChange}
            isDisabled={props.isDisabled}
          />
        </Box>

        <Show when={isMileageHistoryEnabled}>
          <VStack>
            <Show when={isNotNil(props.label)}>
              <Space vertical={5} />
            </Show>

            <IconButton
              icon="devices/access_time"
              priority="tertiary"
              isDisabled={props.isDisabled || isNilOrEmpty(props.vehicleId)}
              onClick={handleOpenOdometerHistory}
              data-testid={suffixTestId(`${props.label ?? 'milage'}-view-history`, props)}
            />
          </VStack>
        </Show>
      </HStack>

      <Show when={isMileageHistoryEnabled}>
        <Show when={didUserChangedMileage && !isOverflowing}>
          <Show when={shouldDisplayDelta}>
            <Text size="xSmall" color="success">
              {i18n.t('page.rentalOrder.labels.delta', {
                delta: formatNumber(vehicleMileage?.delta ?? 0),
              })}
            </Text>
          </Show>

          <Show when={shouldDisplayNoPriorMileage}>
            <HStack spacing={2} align="center">
              <Icon value="action/info_outline" size={3} color="text.link" />
              <Text size="xSmall" color="link">
                {i18n.t('page.rentalOrder.labels.noPriorMilage')}
              </Text>
            </HStack>
          </Show>

          <Show when={shouldDisplayWarning}>
            <HStack spacing={2} align="center">
              <Icon value="alert/warning" size={3} color="text.warning" />
              <Text size="xSmall" color="warning">
                {i18n.t('page.rentalOrder.labels.warning', {
                  actualMilage: formatNumber(vehicleMileage?.actualMileage ?? 0),
                  delta: formatNumber(vehicleMileage?.delta ?? 0),
                })}
              </Text>
            </HStack>
          </Show>
        </Show>
      </Show>
    </VStack>
  );
}
